import { Style, width, joinHorizontal, joinVertical } from '@boba-cli/chapstick';
import { ViewportModel } from '@boba-cli/viewport';

// src/model.ts
function defaultStyles() {
  const keyStyle = new Style().foreground("#626262");
  const descStyle = new Style().foreground("#4A4A4A");
  const sepStyle = new Style().foreground("#3C3C3C");
  return {
    shortKey: keyStyle,
    shortDesc: descStyle,
    shortSeparator: sepStyle,
    ellipsis: sepStyle,
    fullKey: keyStyle,
    fullDesc: descStyle,
    fullSeparator: sepStyle
  };
}

// src/model.ts
var HelpModel = class _HelpModel {
  width;
  showAll;
  shortSeparator;
  fullSeparator;
  ellipsis;
  styles;
  constructor(options = {}) {
    this.width = options.width ?? 0;
    this.showAll = options.showAll ?? false;
    this.shortSeparator = options.shortSeparator ?? " \u2022 ";
    this.fullSeparator = options.fullSeparator ?? "    ";
    this.ellipsis = options.ellipsis ?? "\u2026";
    const defaults = defaultStyles();
    this.styles = { ...defaults, ...options.styles };
  }
  /** Create a new help model with defaults applied. */
  static new(options = {}) {
    return new _HelpModel(options);
  }
  /** Return a new model with updated width. */
  withWidth(width) {
    return this.with({ width });
  }
  /** Return a new model with showAll toggled. */
  withShowAll(showAll) {
    return this.with({ showAll });
  }
  /** Tea update: no-op (view-only component). */
  update(_msg) {
    return [this, null];
  }
  /** Render help text from the provided key map. */
  view(keyMap) {
    if (this.showAll) {
      return this.fullHelpView(keyMap.fullHelp());
    }
    return this.shortHelpView(keyMap.shortHelp());
  }
  /** Render single-line help. */
  shortHelpView(bindings) {
    if (!bindings || bindings.length === 0) {
      return "";
    }
    let result = "";
    let totalWidth = 0;
    const separator = this.styles.shortSeparator.inline(true).render(this.shortSeparator);
    for (const [i, kb] of bindings.entries()) {
      if (!kb.enabled()) continue;
      const sep = totalWidth > 0 && i < bindings.length ? separator : "";
      const help = kb.help();
      const item = sep + this.styles.shortKey.inline(true).render(help.key) + " " + this.styles.shortDesc.inline(true).render(help.desc);
      const itemWidth = width(item);
      const [tail, ok] = this.shouldAddItem(totalWidth, itemWidth);
      if (!ok) {
        if (tail) {
          result += tail;
        }
        break;
      }
      totalWidth += itemWidth;
      result += item;
    }
    return result;
  }
  /** Render multi-column help. */
  fullHelpView(groups) {
    if (!groups || groups.length === 0) {
      return "";
    }
    const out = [];
    let totalWidth = 0;
    const separator = this.styles.fullSeparator.inline(true).render(this.fullSeparator);
    for (const [i, group] of groups.entries()) {
      if (!group || !shouldRenderColumn(group)) {
        continue;
      }
      const keys = [];
      const descriptions = [];
      for (const kb of group) {
        if (!kb.enabled()) continue;
        const help = kb.help();
        keys.push(this.styles.fullKey.render(help.key));
        descriptions.push(this.styles.fullDesc.render(help.desc));
      }
      const sep = totalWidth > 0 && i < groups.length ? separator : "";
      const parts = sep ? [sep, keys.join("\n"), " ", descriptions.join("\n")] : [keys.join("\n"), " ", descriptions.join("\n")];
      const column = joinHorizontal(...parts);
      const columnWidth = width(column);
      const [tail, ok] = this.shouldAddItem(totalWidth, columnWidth);
      if (!ok) {
        if (tail) {
          out.push(tail);
        }
        break;
      }
      totalWidth += columnWidth;
      out.push(column);
    }
    return joinHorizontal(...out);
  }
  shouldAddItem(totalWidth, width$1) {
    if (this.width > 0 && totalWidth + width$1 > this.width) {
      const tail = " " + this.styles.ellipsis.inline(true).render(this.ellipsis);
      if (totalWidth + width(tail) < this.width) {
        return [tail, false];
      }
    }
    return ["", true];
  }
  with(patch) {
    return new _HelpModel({
      width: this.width,
      showAll: this.showAll,
      shortSeparator: this.shortSeparator,
      fullSeparator: this.fullSeparator,
      ellipsis: this.ellipsis,
      styles: this.styles,
      ...patch
    });
  }
};
function shouldRenderColumn(bindings) {
  return bindings.some((b) => b.enabled());
}
var KEY_WIDTH = 12;
function generateHelpScreen(title, titleColor, entries, width, height) {
  let helpScreen = "";
  for (const content of entries) {
    const paddedKey = content.key.padEnd(KEY_WIDTH);
    const keyText = new Style().bold(true).foreground({ dark: "#ffffff", light: "#000000" }).render(paddedKey);
    const descriptionText = new Style().foreground({ dark: "#ffffff", light: "#000000" }).render(content.description);
    const row = joinHorizontal(2, keyText, descriptionText);
    helpScreen += `${row}
`;
  }
  const titleText = new Style().bold(true).background(titleColor.background).foreground(titleColor.foreground).padding(0, 1).italic(true).render(title);
  return new Style().width(width).height(height).render(joinVertical(titleText, helpScreen));
}
var HelpBubble = class _HelpBubble {
  /**
   * The viewport model that handles scrolling and content display.
   */
  viewport;
  /**
   * The array of help entries displayed in the bubble.
   */
  entries;
  /**
   * The title text shown at the top of the help screen.
   */
  title;
  /**
   * The color configuration for the title bar.
   */
  titleColor;
  /**
   * Whether the help bubble is active and receiving input.
   */
  active;
  constructor(viewport, entries, title, titleColor, active) {
    this.viewport = viewport;
    this.entries = entries;
    this.title = title;
    this.titleColor = titleColor;
    this.active = active;
  }
  /**
   * Create a new help bubble.
   * @param active - Whether the component receives input
   * @param title - Title text for the help screen
   * @param titleColor - Color configuration for the title
   * @param entries - Array of help entries to display
   */
  static new(active, title, titleColor, entries) {
    const viewport = ViewportModel.new({ width: 0, height: 0 });
    const content = generateHelpScreen(title, titleColor, entries, 0, 0);
    const initializedViewport = viewport.setContent(content);
    return new _HelpBubble(
      initializedViewport,
      entries,
      title,
      titleColor,
      active
    );
  }
  /**
   * Set the size of the help bubble and regenerate content.
   * @param width - Width in characters
   * @param height - Height in lines
   */
  setSize(width, height) {
    const content = generateHelpScreen(
      this.title,
      this.titleColor,
      this.entries,
      width,
      height
    );
    const updatedViewport = this.viewport.setWidth(width).setHeight(height).setContent(content);
    return new _HelpBubble(
      updatedViewport,
      this.entries,
      this.title,
      this.titleColor,
      this.active
    );
  }
  /**
   * Set whether the component is active (receives input).
   * @param active - Active state
   */
  setIsActive(active) {
    if (this.active === active) return this;
    return new _HelpBubble(
      this.viewport,
      this.entries,
      this.title,
      this.titleColor,
      active
    );
  }
  /**
   * Set the title color and regenerate content.
   * @param color - New title color configuration
   */
  setTitleColor(color) {
    const content = generateHelpScreen(
      this.title,
      color,
      this.entries,
      this.viewport.width,
      this.viewport.height
    );
    const updatedViewport = this.viewport.setContent(content);
    return new _HelpBubble(
      updatedViewport,
      this.entries,
      this.title,
      color,
      this.active
    );
  }
  /**
   * Scroll to the top of the viewport.
   */
  gotoTop() {
    const updatedViewport = this.viewport.scrollToTop();
    if (updatedViewport === this.viewport) return this;
    return new _HelpBubble(
      updatedViewport,
      this.entries,
      this.title,
      this.titleColor,
      this.active
    );
  }
  /**
   * Handle Tea update messages (viewport scrolling when active).
   * @param msg - Tea message
   */
  update(msg) {
    if (!this.active) {
      return [this, null];
    }
    const [updatedViewport, cmd] = this.viewport.update(msg);
    if (updatedViewport === this.viewport) {
      return [this, cmd];
    }
    return [
      new _HelpBubble(
        updatedViewport,
        this.entries,
        this.title,
        this.titleColor,
        this.active
      ),
      cmd
    ];
  }
  /**
   * Render the help screen.
   */
  view() {
    return this.viewport.view();
  }
};

export { HelpBubble, HelpModel, defaultStyles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map